import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Sunrise, Sun, Moon, Feather, Plus, Trash2, Search, BookOpen, PenLine, CalendarDays, ChevronLeft, ChevronRight, MessagesSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;
const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const localToday = () => new Date().toLocaleDateString("sv-SE");

const DAILY = [
  { type: "morning", icon: Sunrise, promptKey: "journal.my.morningPrompt", labelKey: "my.morning" },
  { type: "practice", icon: Sun, promptKey: "journal.my.practicePrompt", labelKey: "my.practice" },
  { type: "evening", icon: Moon, promptKey: "journal.my.eveningPrompt", labelKey: "my.evening" },
];
const TYPE_META = {
  morning: { icon: Sunrise, labelKey: "my.morning" },
  practice: { icon: Sun, labelKey: "my.practice" },
  evening: { icon: Moon, labelKey: "my.evening" },
  free: { icon: Feather, labelKey: "journal.my.freeLabel" },
};

function SaveState({ state, t, onView }) {
  if (!state) return null;
  return (
    <span className="flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase">
      {state === "saving" ? (
        <span className="text-bone/40">{t("journal.my.saving")}</span>
      ) : (
        <>
          <span className="text-gold/80" data-testid="saved-in-journal">{t("my.journal.savedIn")}</span>
          {onView && (
            <button type="button" onClick={onView} data-testid="view-in-journal-link"
              className="text-bone/60 underline underline-offset-2 hover:text-gold normal-case tracking-normal">
              {t("my.journal.viewIn")}
            </button>
          )}
        </>
      )}
    </span>
  );
}

function useAutosave(saveFn, delay = 1200) {
  const timer = useRef(null);
  const pending = useRef(null);
  const saveRef = useRef(saveFn);
  useEffect(() => { saveRef.current = saveFn; }, [saveFn]);
  const [state, setState] = useState(null);
  const trigger = useCallback((...args) => {
    setState("saving");
    pending.current = args;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      pending.current = null;
      try {
        await saveRef.current(...args);
        setState("saved");
      } catch (_) {
        setState(null);
      }
    }, delay);
  }, [delay]);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (pending.current) saveRef.current(...pending.current).catch(() => {});
  }, []);
  return [state, trigger];
}

function DailySection({ type, icon: Icon, promptKey, labelKey, date, initial, t, onView }) {
  const [content, setContent] = useState(initial || "");
  useEffect(() => { setContent(initial || ""); }, [initial]);
  const save = useCallback(
    (value) => axios.put(`${API}/api/me/journal/day/${type}`, { date, content: value }, { withCredentials: true }),
    [type, date]
  );
  const [state, trigger] = useAutosave(save);
  return (
    <section data-testid={`journal-section-${type}`} className="border border-white/10 bg-[#0b0b0b] p-6 md:p-8">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <h2 className="flex items-center gap-3 text-[12px] tracking-[0.24em] uppercase text-gold">
          <Icon size={16} strokeWidth={1.5} /> {t(labelKey)}
        </h2>
        <SaveState state={state} t={t} onView={onView} />
      </div>
      <p style={serif} className="text-lg text-bone/50 italic mb-5 leading-relaxed">{t(promptKey)}</p>
      <textarea
        data-testid={`journal-textarea-${type}`}
        value={content}
        onChange={(e) => { setContent(e.target.value); trigger(e.target.value); }}
        placeholder={t("journal.my.writePh")}
        rows={5}
        className="w-full bg-black/40 border border-white/10 focus:border-gold/50 outline-none p-4 text-bone/90 text-sm leading-relaxed placeholder:text-bone/25 resize-y transition-colors"
      />
    </section>
  );
}

function FreeComposer({ t, onView }) {
  const [active, setActive] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const save = useCallback(async (tl, ct) => {
    if (!active) return;
    await axios.put(`${API}/api/me/journal/free/${active}`, { title: tl, content: ct }, { withCredentials: true });
  }, [active]);
  const [state, trigger] = useAutosave(save);

  const createEntry = async () => {
    const { data } = await axios.post(`${API}/api/me/journal/free`, { title: "", content: "" }, { withCredentials: true });
    setActive(data.entry.id);
    setTitle("");
    setContent("");
  };
  return (
    <section data-testid="journal-section-free" className="border border-white/10 bg-[#0b0b0b] p-6 md:p-8">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <h2 className="flex items-center gap-3 text-[12px] tracking-[0.24em] uppercase text-gold">
          <Feather size={16} strokeWidth={1.5} /> {t("journal.my.freeLabel")}
        </h2>
        <SaveState state={state} t={t} onView={onView} />
      </div>
      <p style={serif} className="text-lg text-bone/50 italic mb-5 leading-relaxed">{t("journal.my.freePrompt")}</p>
      {!active ? (
        <button onClick={createEntry} data-testid="journal-new-free"
          className="inline-flex items-center gap-2 min-h-[44px] px-5 py-2.5 border border-gold/40 text-gold text-[11px] tracking-[0.22em] uppercase hover:bg-gold/10 transition-colors">
          <Plus size={14} strokeWidth={1.5} /> {t("my.journal.newFree")}
        </button>
      ) : (
        <div className="space-y-3">
          <input value={title} data-testid="journal-free-title" placeholder={t("my.journal.freeTitlePh")}
            onChange={(e) => { setTitle(e.target.value); trigger(e.target.value, content); }}
            className="w-full bg-black/40 border border-white/10 focus:border-gold/50 outline-none p-3 text-bone text-sm placeholder:text-bone/25" />
          <textarea value={content} data-testid="journal-free-content" rows={6} placeholder={t("journal.my.writePh")}
            onChange={(e) => { setContent(e.target.value); trigger(title, e.target.value); }}
            className="w-full bg-black/40 border border-white/10 focus:border-gold/50 outline-none p-4 text-bone/90 text-sm leading-relaxed placeholder:text-bone/25 resize-y" />
          <button onClick={createEntry} data-testid="journal-another-free"
            className="text-[10px] tracking-[0.2em] uppercase text-bone/50 hover:text-gold">
            <Plus size={11} className="inline mr-1" /> {t("my.journal.newFree")}
          </button>
        </div>
      )}
    </section>
  );
}

const FILTERS = ["all", "morning", "practice", "evening", "free"];

function JournalCalendar({ selected, onSelect, t }) {
  const [month, setMonth] = useState(() => (selected || localToday()).slice(0, 7));
  const [marked, setMarked] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/me/journal/archive/dates`, { params: { month }, withCredentials: true })
      .then(({ data }) => setMarked(data.dates || []))
      .catch(() => setMarked([]));
  }, [month]);

  const [y, m] = month.split("-").map(Number);
  const startDow = (new Date(y, m - 1, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(y, m, 0).getDate();
  const fmt = (d) => `${month}-${String(d).padStart(2, "0")}`;
  const today = localToday();
  const nav = (delta) => {
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const goToday = () => { setMonth(today.slice(0, 7)); onSelect(today); };
  const monthLabel = new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const dows = ["M", "T", "W", "T", "F", "S", "S"];

  return (
    <div data-testid="journal-calendar" className="border border-white/10 bg-[#0b0b0b] p-4 md:p-5">
      <div className="flex items-center justify-between gap-2 mb-3">
        <button onClick={() => nav(-1)} data-testid="calendar-prev" aria-label="Previous month"
          className="min-h-[40px] min-w-[40px] flex items-center justify-center border border-white/15 text-bone/60 hover:border-gold/40 hover:text-gold transition-colors">
          <ChevronLeft size={15} strokeWidth={1.5} />
        </button>
        <span className="text-[11px] tracking-[0.22em] uppercase text-bone/80" data-testid="calendar-month-label">{monthLabel}</span>
        <div className="flex items-center gap-2">
          <button onClick={goToday} data-testid="calendar-today"
            className="min-h-[40px] px-3 border border-gold/40 text-gold text-[10px] tracking-[0.18em] uppercase hover:bg-gold/10 transition-colors">
            {t("my.journal.todayBtn")}
          </button>
          <button onClick={() => nav(1)} data-testid="calendar-next" aria-label="Next month"
            className="min-h-[40px] min-w-[40px] flex items-center justify-center border border-white/15 text-bone/60 hover:border-gold/40 hover:text-gold transition-colors">
            <ChevronRight size={15} strokeWidth={1.5} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {dows.map((d, i) => (
          <span key={i} className="text-[9px] tracking-[0.2em] uppercase text-bone/30 py-1">{d}</span>
        ))}
        {Array.from({ length: startDow }, (_, i) => <span key={`pad-${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const dateStr = fmt(i + 1);
          const has = marked.includes(dateStr);
          const isSel = selected === dateStr;
          const isToday = dateStr === today;
          return (
            <button key={dateStr} data-testid={`calendar-day-${dateStr}`}
              onClick={() => onSelect(isSel ? "" : dateStr)}
              className={`relative min-h-[40px] flex flex-col items-center justify-center text-sm transition-colors ${
                isSel ? "bg-gold/20 text-gold border border-gold"
                : has ? "text-bone border border-gold/30 hover:border-gold/60"
                : "text-bone/40 border border-transparent hover:border-white/20"} ${isToday && !isSel ? "underline underline-offset-4 decoration-gold/60" : ""}`}>
              {i + 1}
              {has && <span className="absolute bottom-1 w-1 h-1 rounded-full bg-gold" data-testid={`calendar-dot-${dateStr}`} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Archive({ t }) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [open, setOpen] = useState(null); // entry being edited
  const [confirmDel, setConfirmDel] = useState(null);
  const [showCal, setShowCal] = useState(false);
  const debounce = useRef(null);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/me/journal/archive`, {
      params: { q, entry_type: filter, date, page, limit: 30 }, withCredentials: true });
    setEntries(data.entries || []);
    setTotal(data.total || 0);
    setPages(data.pages || 1);
  }, [q, filter, date, page]);
  useEffect(() => { load(); }, [load]);

  const onSearch = (v) => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { setQ(v); setPage(1); }, 350);
  };

  const remove = async (entry) => {
    await axios.delete(`${API}/api/me/journal/entry/${entry.id}`, { withCredentials: true });
    setConfirmDel(null);
    setOpen(null);
    load();
  };

  if (open) return <EntryEditor entry={open} t={t} onBack={() => { setOpen(null); load(); }} />;

  const groups = [];
  entries.forEach((e) => {
    const last = groups[groups.length - 1];
    if (last && last.date === e.date) last.items.push(e);
    else groups.push({ date: e.date, items: [e] });
  });

  return (
    <div data-testid="journal-archive" className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} strokeWidth={1.5} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-bone/40 pointer-events-none" />
          <input type="text" defaultValue={q} onChange={(e) => onSearch(e.target.value)}
            placeholder={t("my.journal.searchPh")} data-testid="archive-search"
            className="w-full bg-black/40 border border-white/15 focus:border-gold/50 outline-none pl-10 pr-3 py-3 text-bone text-sm placeholder:text-bone/30" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowCal((v) => !v)} data-testid="archive-calendar-toggle"
            className={`inline-flex items-center gap-2 min-h-[44px] px-4 text-[10px] tracking-[0.2em] uppercase border transition-colors ${
              showCal || date ? "border-gold text-gold bg-gold/10" : "border-white/15 text-bone/60 hover:border-gold/40"}`}>
            <CalendarDays size={14} strokeWidth={1.5} /> {t("my.journal.calendar")}
          </button>
          {date && (
            <button onClick={() => { setDate(""); setPage(1); }} data-testid="archive-date-clear"
              className="text-[10px] tracking-[0.18em] uppercase text-bone/50 hover:text-gold whitespace-nowrap min-h-[44px] px-2">
              {t("my.journal.allDates")}
            </button>
          )}
        </div>
      </div>

      {showCal && (
        <JournalCalendar selected={date} t={t}
          onSelect={(d) => { setDate(d); setPage(1); }} />
      )}

      <div className="flex flex-wrap gap-2" data-testid="archive-filters">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => { setFilter(f); setPage(1); }} data-testid={`archive-filter-${f}`}
            className={`min-h-[40px] px-4 py-2 text-[10px] tracking-[0.2em] uppercase border transition-colors ${
              filter === f ? "border-gold text-gold bg-gold/10" : "border-white/15 text-bone/60 hover:border-gold/40"}`}>
            {f === "all" ? t("my.journal.filterAll") : t(TYPE_META[f].labelKey)}
          </button>
        ))}
      </div>

      <p className="text-[10px] tracking-[0.2em] uppercase text-bone/40" data-testid="archive-count">
        {total} {t("my.journal.entriesCount")}
      </p>

      {groups.length === 0 && (
        <p className="text-bone/40 text-sm py-8" data-testid="archive-empty">{t("my.journal.empty")}</p>
      )}
      {groups.map((g) => (
        <div key={g.date} data-testid={`archive-day-${g.date}`}>
          <h3 className="text-[11px] tracking-[0.24em] uppercase text-gold/80 border-b border-white/10 pb-2 mb-3">{g.date}</h3>
          <div className="space-y-2">
            {g.items.map((e) => {
              const Meta = TYPE_META[e.entry_type] || TYPE_META.free;
              return (
                <div key={e.id} className="border border-white/10 bg-[#0b0b0b] hover:border-gold/30 transition-colors">
                  <button onClick={() => setOpen(e)} data-testid={`archive-entry-${e.id}`}
                    className="w-full text-left p-4 min-h-[44px]">
                    <span className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-gold/70 mb-1.5">
                      <Meta.icon size={12} strokeWidth={1.5} /> {t(Meta.labelKey)}
                    </span>
                    {e.title && <span className="block text-bone text-sm font-medium mb-0.5">{e.title}</span>}
                    <span className="block text-bone/60 text-sm truncate">{(e.content || "").slice(0, 140) || "—"}</span>
                  </button>
                  <div className="flex justify-end px-3 pb-2">
                    {confirmDel === e.id ? (
                      <span className="flex items-center gap-2 text-[10px] tracking-[0.14em] uppercase">
                        <span className="text-red-300">{t("my.journal.deleteConfirm")}</span>
                        <button onClick={() => remove(e)} data-testid={`archive-delete-yes-${e.id}`} className="text-red-400 hover:text-red-300 min-h-[36px] px-2 border border-red-400/40">{t("my.journal.deleteYes")}</button>
                        <button onClick={() => setConfirmDel(null)} className="text-bone/50 min-h-[36px] px-2">{t("my.journal.cancel")}</button>
                      </span>
                    ) : (
                      <button onClick={() => setConfirmDel(e.id)} data-testid={`archive-delete-${e.id}`}
                        title={t("my.journal.deleteYes")}
                        className="p-2 text-bone/40 hover:text-red-400 min-h-[36px]"><Trash2 size={14} strokeWidth={1.5} /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {pages > 1 && (
        <div className="flex items-center gap-3 pt-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="archive-prev"
            className="min-h-[44px] px-5 border border-white/15 text-bone/60 text-[10px] tracking-[0.2em] uppercase disabled:opacity-30 hover:border-gold/40">←</button>
          <span className="text-[10px] tracking-[0.2em] uppercase text-bone/40">{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="archive-next"
            className="min-h-[44px] px-5 border border-white/15 text-bone/60 text-[10px] tracking-[0.2em] uppercase disabled:opacity-30 hover:border-gold/40">→</button>
        </div>
      )}
    </div>
  );
}

function EntryEditor({ entry, t, onBack }) {
  const [title, setTitle] = useState(entry.title || "");
  const [content, setContent] = useState(entry.content || "");
  const [confirmDel, setConfirmDel] = useState(false);
  const isFree = entry.entry_type === "free";
  const Meta = TYPE_META[entry.entry_type] || TYPE_META.free;

  const save = useCallback((tl, ct) => {
    // Same entry_id preserved: daily via date+type upsert, free via id.
    return isFree
      ? axios.put(`${API}/api/me/journal/free/${entry.id}`, { title: tl, content: ct }, { withCredentials: true })
      : axios.put(`${API}/api/me/journal/day/${entry.entry_type}`, { date: entry.date, content: ct }, { withCredentials: true });
  }, [entry, isFree]);
  const [state, trigger] = useAutosave(save);

  const remove = async () => {
    await axios.delete(`${API}/api/me/journal/entry/${entry.id}`, { withCredentials: true });
    onBack();
  };

  return (
    <div data-testid="journal-entry-editor" className="border border-white/10 bg-[#0b0b0b] p-5 md:p-8 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={onBack} data-testid="editor-back"
          className="inline-flex items-center gap-2 min-h-[44px] text-[11px] tracking-[0.22em] uppercase text-bone/60 hover:text-gold">
          <ArrowLeft size={13} strokeWidth={1.5} /> {t("my.journal.back")}
        </button>
        <SaveState state={state} t={t} />
      </div>
      <p className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-gold/80">
        <Meta.icon size={13} strokeWidth={1.5} /> {t(Meta.labelKey)} · {entry.date}
      </p>
      {isFree && (
        <input value={title} data-testid="editor-title" placeholder={t("my.journal.freeTitlePh")}
          onChange={(e) => { setTitle(e.target.value); trigger(e.target.value, content); }}
          className="w-full bg-black/40 border border-white/10 focus:border-gold/50 outline-none p-3 text-bone text-sm placeholder:text-bone/25" />
      )}
      <textarea value={content} data-testid="editor-content" rows={10}
        onChange={(e) => { setContent(e.target.value); trigger(title, e.target.value); }}
        className="w-full bg-black/40 border border-white/10 focus:border-gold/50 outline-none p-4 text-bone/90 text-sm leading-relaxed resize-y" />
      <div className="flex justify-end">
        {confirmDel ? (
          <span className="flex items-center gap-2 text-[10px] tracking-[0.14em] uppercase">
            <span className="text-red-300">{t("my.journal.deleteConfirm")}</span>
            <button onClick={remove} data-testid="editor-delete-yes" className="text-red-400 border border-red-400/40 min-h-[40px] px-3">{t("my.journal.deleteYes")}</button>
            <button onClick={() => setConfirmDel(false)} className="text-bone/50 min-h-[40px] px-2">{t("my.journal.cancel")}</button>
          </span>
        ) : (
          <button onClick={() => setConfirmDel(true)} data-testid="editor-delete"
            className="inline-flex items-center gap-2 min-h-[40px] px-4 text-[10px] tracking-[0.2em] uppercase text-bone/40 hover:text-red-400 border border-white/10">
            <Trash2 size={13} strokeWidth={1.5} /> {t("my.journal.deleteYes")}
          </button>
        )}
      </div>
    </div>
  );
}

function MentorReviewsPlaceholder({ t }) {
  // Preparation only: future mentor responses reference Journal entry_id; no storage yet.
  return (
    <section data-testid="mentor-reviews-placeholder" className="border border-white/10 bg-[#0b0b0b] p-6 md:p-10 text-center space-y-4">
      <MessagesSquare size={28} strokeWidth={1.25} className="mx-auto text-gold/60" />
      <h2 style={serif} className="text-2xl text-bone">{t("my.mentor.title")}</h2>
      <p className="text-bone/70 text-sm" data-testid="mentor-empty-state">{t("my.mentor.empty")}</p>
      <p style={serif} className="text-lg italic text-bone/45 leading-relaxed max-w-md mx-auto" data-testid="mentor-info-text">
        {t("my.mentor.info")}
      </p>
    </section>
  );
}

export default function MyJournal() {
  const { user, loading } = useAuth();
  const { t } = useT();
  const [tab, setTab] = useState("today");
  const [day, setDay] = useState(null);
  const today = localToday();

  useEffect(() => {
    if (!user || tab !== "today") return;
    axios.get(`${API}/api/me/journal/day`, { params: { date: today }, withCredentials: true })
      .then(({ data }) => setDay(data))
      .catch(() => setDay({ entries: {} }));
  }, [user, today, tab]);

  if (loading) return null;
  if (!user) return <Navigate to="/my-stoicos" replace />;

  const goArchive = () => setTab("archive");

  return (
    <main className="min-h-screen bg-ink pt-28 pb-32 px-5 md:px-10">
      <div className="max-w-3xl mx-auto space-y-8">
        <Link to="/my-stoicos" data-testid="journal-back-link"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-bone/50 hover:text-gold transition-colors">
          <ArrowLeft size={12} strokeWidth={1.5} /> My StoicOs
        </Link>
        <header>
          <h1 style={serif} className="text-3xl md:text-4xl text-bone">{t("my.journal.title")}</h1>
          <p className="mt-2 text-bone/50 text-sm">{today}</p>
        </header>

        <div className="flex flex-wrap gap-2" data-testid="journal-tabs">
          {[["today", PenLine, t("my.journal.tabToday")], ["archive", BookOpen, t("my.journal.tabArchive")], ["mentor", MessagesSquare, t("my.journal.tabMentor")]].map(([key, Icon, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`journal-tab-${key}`}
              className={`inline-flex items-center gap-2 min-h-[44px] px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase border transition-colors ${
                tab === key ? "border-gold text-gold bg-gold/10" : "border-white/15 text-bone/60 hover:border-gold/40"}`}>
              <Icon size={14} strokeWidth={1.5} /> {label}
            </button>
          ))}
        </div>

        {tab === "today" ? (
          <div className="space-y-6">
            {DAILY.map((s) => (
              <DailySection key={s.type} {...s} date={today} t={t} onView={goArchive}
                initial={day?.entries?.[s.type]?.content} />
            ))}
            <FreeComposer t={t} onView={goArchive} />
          </div>
        ) : tab === "mentor" ? (
          <MentorReviewsPlaceholder t={t} />
        ) : (
          <Archive t={t} />
        )}
      </div>
    </main>
  );
}
